#include "_prelude_fog.vertex.glsl"
#include "_prelude_shadow.vertex.glsl"

in vec3 a_pos_3f;
in ivec4 a_normal_3;
in ivec4 a_centroid_3;

#ifdef FLOOD_LIGHT
in int a_flood_light_wall_radius_1i16;
#endif

#ifdef BUILDING_FAUX_FACADE
in uvec4 a_faux_facade_data;
in uvec2 a_faux_facade_vertical_range;
#endif

uniform mat4 u_matrix;
uniform mat4 u_normal_matrix;
uniform highp float u_tile_to_meter;

#ifdef RENDER_FRONT_CUTOFF
uniform vec3 u_front_cutoff_params; // [start, range, opacity]
out float v_front_cutoff_opacity;
#endif

#ifdef INDICATOR_CUTOUT
#ifdef FEATURE_CUTOUT
out vec4 v_ground_roof;
#endif
#endif

out vec4 v_color;
out vec3 v_normal;
out highp vec3 v_pos;


#ifdef BUILDING_FAUX_FACADE
out lowp float v_faux_facade;
out highp float v_faux_facade_ed;
out highp vec2 v_faux_facade_window;
out highp vec2 v_faux_facade_floor;
out highp vec2 v_faux_facade_range;
out highp float v_aspect;
out highp vec3 v_tbn_0;
out highp vec3 v_tbn_1;
out highp vec3 v_tbn_2;
out highp vec4 v_faux_color_emissive;
#endif

#ifdef RENDER_SHADOWS
uniform mat4 u_light_matrix_0;
uniform mat4 u_light_matrix_1;
out highp vec4 v_pos_light_view_0;
out highp vec4 v_pos_light_view_1;
#endif

#ifdef FLOOD_LIGHT
out highp float v_flood_radius; // in tile coordinates
out float v_has_flood_light;
#endif

const float MAX_UINT_16 = 65535.0;
const float MAX_INT_16 = 32767.0;
const float MAX_UINT_8 = 255.0;
const float TWO_POW_8 = 256.0;
const float FLOOD_LIGHT_MAX_RADIUS_METER = 2048.0;

vec3 sRGBToLinear(vec3 srgbIn) {
    return pow(srgbIn, vec3(2.2));
}

#ifdef BUILDING_FAUX_FACADE
mat3 get_tbn(in vec3 normal) {
    const vec3 bitangent = vec3(0.0, 0.0, 1.0);
    vec3 tangent = normalize(vec3(normal.y, -normal.x, 0.0)); // -cross(bitangent, normal);
    return mat3(tangent, bitangent, normal);
}
#endif
#pragma mapbox: define-attribute-vertex-shader-only highp uvec2 part_color_emissive
#pragma mapbox: define-attribute-vertex-shader-only highp uvec2 faux_facade_color_emissive

void main() {
    #pragma mapbox: initialize-attribute-custom highp uvec2 part_color_emissive
    #pragma mapbox: initialize-attribute-custom highp uvec2 faux_facade_color_emissive

#ifdef FLOOD_LIGHT
    v_flood_radius = (float(a_flood_light_wall_radius_1i16) / MAX_INT_16 * FLOOD_LIGHT_MAX_RADIUS_METER);
    v_has_flood_light = step(0.0, v_flood_radius);
#endif

    vec4 color_emissive = decode_color(vec2(part_color_emissive));
    v_color = vec4(sRGBToLinear(color_emissive.rgb), color_emissive.a);

    vec3 a_normal_3f = vec3(a_normal_3) / MAX_INT_16;
    v_normal = vec3(u_normal_matrix * vec4(a_normal_3f, 0.0));

    float hidden = 0.0;
    float depth_offset = 0.0;

    // Decode centroid position and span from packed attribute.
    // Encoding: cx_encoded = clamp(cx, 0, 8191) * 4 + spanXbits (2 bits per axis)
    vec2 centroid_xy = vec2(a_centroid_3.xy >> 2);
    vec2 spanBits = vec2(a_centroid_3.xy & 3);

#ifdef BUILDING_FAUX_FACADE
    vec4 faux_facade_data = vec4(a_faux_facade_data);
    v_faux_facade = faux_facade_data.x;
    if (v_faux_facade > 0.0) {
        v_faux_facade_ed = faux_facade_data.x  * u_tile_to_meter;

        float window_x_perc = floor(faux_facade_data.y / TWO_POW_8);
        float window_y_perc = faux_facade_data.y - TWO_POW_8 * window_x_perc;
        vec2 window_perc = vec2(window_x_perc, window_y_perc) / MAX_UINT_8;

        v_faux_facade_floor = (faux_facade_data.zw / MAX_UINT_16 * EXTENT) * u_tile_to_meter;
        v_faux_facade_window = window_perc * v_faux_facade_floor;

        v_faux_facade_range = (vec2(a_faux_facade_vertical_range) / MAX_UINT_16 * EXTENT) * u_tile_to_meter;

        v_aspect = v_faux_facade_window.x / v_faux_facade_window.y;

        mat3 tbn = get_tbn(normalize(v_normal));
        v_tbn_0 = tbn[0];
        v_tbn_1 = tbn[1];
        v_tbn_2 = tbn[2];

        v_faux_color_emissive = decode_color(vec2(faux_facade_color_emissive));
        v_faux_color_emissive.rgb = sRGBToLinear(v_faux_color_emissive.rgb);

        depth_offset = min(1000.0, float(a_centroid_3.z)) * 0.0000002;
    }
#endif
    v_pos = a_pos_3f;

#if defined(RENDER_CUTOFF) || defined(RENDER_FRONT_CUTOFF)
    float halfSpanX = spanBits.x * 10.0 / u_tile_to_meter;
    float halfSpanY = spanBits.y * 10.0 / u_tile_to_meter;
    vec2 screenUpInTile = vec2(u_matrix[0][1], u_matrix[1][1]);
    vec2 spanOffset = vec2(halfSpanX, halfSpanY) * sign(screenUpInTile);
    vec2 cutoff_highestCorner = centroid_xy + spanOffset;
    vec2 cutoff_lowestCorner = centroid_xy - spanOffset;
#endif

#if defined(RENDER_CUTOFF) || defined(RENDER_FRONT_CUTOFF)
    vec4 ground = u_matrix * vec4(centroid_xy, 0.0, 1.0);
#endif

#ifdef RENDER_CUTOFF
    v_cutoff_opacity = cutoff_opacity(u_cutoff_params, ground.z);
    hidden = float(v_cutoff_opacity == 0.0);
    v_pos.z *= v_cutoff_opacity;
#endif

#ifdef RENDER_SHADOWS
    vec3 shadow_pos = v_pos;
#ifdef NORMAL_OFFSET
    vec3 offset = shadow_normal_offset_model(v_normal);
    shadow_pos += offset * shadow_normal_offset_multiplier0();
#endif
    v_pos_light_view_0 = u_light_matrix_0 * vec4(shadow_pos, 1.0);
    v_pos_light_view_1 = u_light_matrix_1 * vec4(shadow_pos, 1.0);
#endif

#ifdef FOG
    v_fog_pos = fog_position(v_pos);
#endif

#ifdef RENDER_FRONT_CUTOFF
    v_front_cutoff_opacity = 1.0;
    {
        hidden = max(hidden, float(ground.w <= 0.0));
        float ndc_y = ground.y / max(ground.w, 0.001);
        float threshold = u_front_cutoff_params.x * 2.0 - 1.0;
        float range_ndc = u_front_cutoff_params.y * 2.0;
        hidden = max(hidden, float(ndc_y < threshold - range_ndc));
        float t = clamp((ndc_y - (threshold - range_ndc)) / max(range_ndc, 0.001), 0.0, 1.0);
        v_front_cutoff_opacity = mix(u_front_cutoff_params.z, 1.0, t);
    }
#endif

    gl_Position = mix(u_matrix * vec4(v_pos, 1), AWAY, hidden);
    gl_Position.z -= depth_offset * gl_Position.w;

#ifdef INDICATOR_CUTOUT
#ifdef FEATURE_CUTOUT
    vec4 ic_ground = u_matrix * vec4(v_pos.xy, 0.0, 1.0);
    vec4 ic_roof = u_matrix * vec4(v_pos.xy, v_pos.z, 1.0);
    v_ground_roof = vec4(ic_ground.xy / ic_ground.w, ic_roof.xy / ic_roof.w);
#endif
#endif
}
